'use client';

import { useEffect, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Card } from '@/components/ui/card';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { SectionHeading } from '@/components/frontend/common/section-heading';
import { getLocalizedPath } from '@/lib/locale-utils';

interface MLMPlan {
    slug: string;
    name: string;
    description: string;
    content?: string;
    image?: string;
}

export function MLMPlansCards({ initialPlans }: { initialPlans?: MLMPlan[] } = {}) {
    const t = useTranslations('mlmPlansPage');
    const locale = useLocale();
    const [plans, setPlans] = useState<MLMPlan[]>(() => initialPlans ?? []);
    const [isLoading, setIsLoading] = useState(!initialPlans?.length);

    useEffect(() => {
        if (initialPlans?.length) {
            setPlans(initialPlans);
            setIsLoading(false);
            return;
        }
        const fetchPlans = async () => {
            try {
                setIsLoading(true);
                const res = await fetch(`/api/mlm-plans?locale=${locale}`, {
                    cache: 'no-store',
                });

                if (!res.ok) {
                    throw new Error('Failed to fetch MLM plans');
                }

                const data = await res.json();
                setPlans(Array.isArray(data) ? data : []);
            } catch (error) {
                setPlans([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPlans();
    }, [locale, initialPlans?.length]);

    if (isLoading) {
        return (
            <SectionWrapper variant="default" padding="lg" className="bg-white dark:bg-black">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div
                                key={i}
                                className="h-96 bg-gray-200 dark:bg-gray-900 rounded-2xl animate-pulse"
                            />
                        ))}
                    </div>
                </div>
            </SectionWrapper>
        );
    }

    if (plans.length === 0) {
        return (
            <SectionWrapper variant="default" padding="lg" className="bg-white dark:bg-black">
                <div className="container mx-auto px-4">
                    <div className="text-center py-16 text-gray-600 dark:text-gray-400">
                        {t('noPlansAvailable')}
                    </div>
                </div>
            </SectionWrapper>
        );
    }

    return (
        <SectionWrapper variant="default" padding="lg" className="bg-white dark:bg-black">
            <div className="container mx-auto px-4">
                <div className="mb-12">
                    <div className="mt-6 text-center max-w-4xl mx-auto">
                        <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-white mb-4">
                            {t('typesTitle')}
                        </h3>
                        <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                            {t('typesDescription')}
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {plans.map((plan, index) => {
                        // Use backend slug directly - no translation needed
                        const href = getLocalizedPath(plan.slug, locale);
                        return (
                            <Card
                                key={plan.slug}
                                title={plan.name}
                                description={plan.description}
                                imageSrc={plan.image}
                                imageAlt={plan.name}
                                href={href}
                                showFooterLink={true}
                                footerLinkText="Explore more"
                                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800"
                                animationDelay={index * 100}
                                imageHeight="h-48"
                                imageWidth="w-auto"
                                imagePadding="py-4"
                            />
                        );
                    })}
                </div>
            </div>
        </SectionWrapper>
    );
}

